import { useState, useEffect, useRef, useCallback } from 'react';
import { logger } from '@/shared/lib';
import { useAuth } from "@/app/providers";
import { PAYMENT_ENDPOINTS, type SSEPaymentEvent } from "@/constants/api.payment";

interface UseSecureSSEOptions {
  cartId: number | null;
  cartType: 'cart' | 'cartBar';
  enabled?: boolean;
  onStatusChange?: (status: string, previousStatus?: string) => void;
  onError?: (error: string) => void;
  onTimeout?: () => void;
}

interface SSEState {
  status: string;
  connected: boolean;
  error: string | null;
  lastUpdate: string | null;
  connectionDuration: number;
}

/**
 * Secure SSE hook using Fetch API with ReadableStream
 * 
 * Security features:
 * - Uses Authorization header (not URL params)
 * - Proper error handling and cleanup
 * - Automatic reconnection with exponential backoff
 * - Connection timeout handling
 * - Memory leak prevention
 */
export const useSecureSSE = ({
  cartId,
  cartType,
  enabled = true,
  onStatusChange,
  onError,
  onTimeout
}: UseSecureSSEOptions) => {
  const { token, currentBuilding } = useAuth();
  
  const [sseState, setSSEState] = useState<SSEState>({
    status: 'EN ATTENTE',
    connected: false,
    error: null,
    lastUpdate: null,
    connectionDuration: 0
  });
  
  // Refs for cleanup and state management
  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const isCleaningUpRef = useRef(false);
  const isConnectingRef = useRef(false);
  
  // Stable callback refs to prevent infinite loops
  const onStatusChangeRef = useRef(onStatusChange);
  const onErrorRef = useRef(onError);
  const onTimeoutRef = useRef(onTimeout);
  
  // Update refs when callbacks change
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);
  
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);
  
  // Configuration
  const maxReconnectAttempts = 3;
  const baseReconnectDelay = 2000; // 2 seconds
  
  // Cleanup function - stable reference
  const cleanup = useCallback(() => {
    if (isCleaningUpRef.current) return;
    isCleaningUpRef.current = true;
    
    logger.warn('🧹 Cleaning up SSE connection');
    
    // Abort any ongoing fetch request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Clear reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Reset connection state
    isConnectingRef.current = false;
    
    // Reset state
    setSSEState(prev => ({ 
      ...prev, 
      connected: false,
      connectionDuration: 0
    }));
    
    isCleaningUpRef.current = false;
  }, []);
  
  // Parse SSE data - stable reference
  const parseSSEData = useCallback((data: string): SSEPaymentEvent | null => {
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error('❌ Failed to parse SSE data:', error);
      return null;
    }
  }, []);
  
  // Handle SSE events - stable reference
  const handleSSEEvent = useCallback((event: SSEPaymentEvent) => {
    logger.warn('📨 SSE event received:', event);
    
    setSSEState(prev => ({
      ...prev,
      status: event.status,
      lastUpdate: event.timestamp,
      error: null,
      connectionDuration: event.connection_duration || prev.connectionDuration
    }));
    
    switch (event.event_type) {
      case 'initial':
        logger.warn(`🔔 Initial status: ${event.status}`);
        break;
        
      case 'status_change':
        logger.warn(`🔄 Status changed: ${event.previous_status} → ${event.status}`);
        onStatusChangeRef.current?.(event.status, event.previous_status);
        
        // Close connection if payment is final
        if (event.status === 'PAYÉ' || event.status === 'REFUSÉ') {
          logger.warn('🏁 Payment completed, closing SSE connection');
          cleanup();
        }
        break;
        
      case 'heartbeat':
        logger.warn('💓 SSE heartbeat received');
        break;
        
      case 'timeout':
        logger.warn('⏰ SSE connection timeout');
        onTimeoutRef.current?.();
        cleanup();
        break;
        
      case 'error':
        console.error('❌ SSE error event:', event.error);
        setSSEState(prev => ({ ...prev, error: event.error || 'Unknown error' }));
        onErrorRef.current?.(event.error || 'Unknown error');
        cleanup();
        break;
    }
  }, [cleanup]);
  
  // Connect to SSE stream - stable reference
  const connectSSE = useCallback(async () => {
    if (!cartId || !currentBuilding || !token || !enabled) {
      return;
    }
    
    if (isCleaningUpRef.current || isConnectingRef.current) {
      return;
    }
    
    // Don't reconnect if we've exceeded max attempts
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      console.warn('🚫 Max SSE reconnect attempts reached');
      setSSEState(prev => ({ 
        ...prev, 
        error: 'Connection failed after multiple attempts',
        connected: false 
      }));
      onErrorRef.current?.('Connection failed after multiple attempts');
      return;
    }
    
    isConnectingRef.current = true;
    
    try {
      console.log(`🔌 Connecting to SSE (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`);
      
      // Create new AbortController for this connection
      abortControllerRef.current = new AbortController();
      
      const streamUrl = PAYMENT_ENDPOINTS.PAYMENT_STREAM(
        currentBuilding.pk,
        cartType,
        cartId
      );
      
      console.log(`🔍 SSE URL: ${streamUrl}`);
      console.log(`🔍 Building: ${currentBuilding.pk}, CartType: ${cartType}, CartId: ${cartId}`);
      
      // Fetch with proper headers (Cache-Control now allowed by CORS)
      const response = await fetch(streamUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        signal: abortControllerRef.current.signal
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      if (!response.body) {
        throw new Error('Response body is null');
      }
      
      console.log('✅ SSE connection established');
      reconnectAttempts.current = 0; // Reset on successful connection
      isConnectingRef.current = false;
      
      setSSEState(prev => ({ 
        ...prev, 
        connected: true, 
        error: null 
      }));
      
      // Read the stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            console.log('📡 SSE stream ended');
            break;
          }
          
          // Decode and buffer the data
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete messages
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer
          
          let eventData = '';
          
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              // Event type is handled in the event data itself
              continue;
            } else if (line.startsWith('data: ')) {
              eventData = line.substring(6);
            } else if (line === '' && eventData) {
              // End of event, process it
              const event = parseSSEData(eventData);
              if (event) {
                handleSSEEvent(event);
              }
              eventData = '';
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
      
    } catch (error: any) {
      isConnectingRef.current = false;
      
      if (error.name === 'AbortError') {
        console.log('🔌 SSE connection aborted');
        return;
      }
      
      console.error('❌ SSE connection error:', error);
      
      setSSEState(prev => ({ 
        ...prev, 
        connected: false, 
        error: error.message 
      }));
      
      // Attempt reconnection with exponential backoff
      reconnectAttempts.current++;
      if (reconnectAttempts.current < maxReconnectAttempts) {
        const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts.current - 1);
        console.log(`🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current}/${maxReconnectAttempts})`);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          if (!isCleaningUpRef.current) {
            connectSSE();
          }
        }, delay);
      } else {
        console.warn('🚫 Max reconnection attempts reached');
        onErrorRef.current?.(error.message);
      }
    }
  }, [cartId, currentBuilding, token, cartType, enabled]);
  
  // Effect to manage SSE connection - stable dependencies
  useEffect(() => {
    if (enabled && cartId && currentBuilding && token) {
      connectSSE();
    } else {
      cleanup();
    }
    
    // Cleanup on unmount or dependency change
    return cleanup;
  }, [cartId, currentBuilding?.pk, token, cartType, enabled]); // Only primitive values
  
  return {
    ...sseState,
    reconnect: connectSSE,
    disconnect: cleanup,
    isMaxReconnectAttemptsReached: reconnectAttempts.current >= maxReconnectAttempts
  };
};
